// Receiver-side heal: when a GC deletes an estimate on their side, the
// estshare__<id> shared doc is removed (see useEstimateMutations.remove
// cascade). This hook runs once per session per uid, scans all local
// estimates flagged `_incomingLocked === true` (or with `_fromLink`),
// and for each checks whether the original estshare__<id> doc still
// exists. If not, we drop the local estimate from state.
//
// Runs once per session per uid.

import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getDoc } from 'firebase/firestore'
import { sharedDoc } from '../lib/firebase/firestore'
import { useAuth } from './useAuth'
import { qk } from './queryKeys'
import { saveSlice } from '../lib/db'
import type { AppState } from '../types/firestore'

const swept = new Set<string>()

export function useCleanupOrphanedReceivedEstimates(): void {
  const { effectiveUid: uid, isReady } = useAuth()
  const qc = useQueryClient()

  useEffect(() => {
    if (!uid || !isReady) return
    if (swept.has(uid)) return
    let cancelled = false
    const t = setTimeout(async () => {
      if (cancelled) return
      swept.add(uid)
      const state = qc.getQueryData<AppState>(qk.appstate(uid))
      if (!state) return

      // Look at incoming-locked estimates whose _fromLink points at an
      // estshare__ doc. If that doc is gone, the sender deleted it on
      // their side → we drop our local copy.
      const candidates = state.estimates
        .map((e) => {
          const raw = e as unknown as { _incomingLocked?: boolean; _fromLink?: string }
          if (!raw._incomingLocked) return null
          if (typeof raw._fromLink !== 'string' || !raw._fromLink) return null
          // Only verify estshare__ prefixed docs — dealLinks are handled by
          // the project cleanup hook.
          if (!raw._fromLink.startsWith('estshare__')) return null
          return { id: e.id, estshareId: raw._fromLink }
        })
        .filter((x): x is { id: number; estshareId: string } => x !== null)
      if (candidates.length === 0) return

      const orphanEstIds: number[] = []
      for (const c of candidates) {
        try {
          const snap = await getDoc(sharedDoc(c.estshareId))
          if (!snap.exists()) {
            orphanEstIds.push(c.id)
          }
        } catch (err) {
          console.warn('[cleanup-received-estimates] verify failed', c.estshareId, err)
        }
      }
      if (orphanEstIds.length === 0) return
      if (cancelled) return

      const orphanSet = new Set(orphanEstIds)
      const newEstimates = state.estimates.filter((e) => !orphanSet.has(e.id))
      const next: AppState = { ...state, estimates: newEstimates }

      qc.setQueryData(qk.appstate(uid), next)
      try {
        await saveSlice(uid, 'estimates', newEstimates)
        console.warn('[cleanup-received-estimates] removed', orphanEstIds.length, 'orphaned received estimates')
      } catch (err) {
        console.warn('[cleanup-received-estimates] save failed', err)
      }
    }, 5000)
    return () => { cancelled = true; clearTimeout(t) }
  }, [uid, isReady, qc])
}
